// ---------------------------------------------------------------------------
// Who is signed in, and what they may do.
//
// Important: this store is a convenience for drawing the interface. It is NOT
// a security boundary. Every permission it reports was decided by the server,
// and the server checks again on every single request. Someone who edits this
// state in their browser gains nothing - the API still refuses them.
//
// The session itself lives in HTTP-only cookies that JavaScript cannot read,
// so there is no token here to steal.
// ---------------------------------------------------------------------------

import { create } from 'zustand';
import { api, ApiError, setSessionLostHandler } from '../lib/apiClient';
import type { AuthPayload, CurrentUser, RoleSummary } from '../types';

export type AuthStatus =
  /// Still asking the server whether a previous session is still valid.
  | 'checking'
  /// Signed in.
  | 'authenticated'
  /// Not signed in. Show the login page.
  | 'guest';

interface AuthState {
  status: AuthStatus;
  user: CurrentUser | null;
  role: RoleSummary | null;
  permissions: Set<string>;
  /// Set when a session ended by itself, so the login page can explain why
  /// rather than appearing without warning mid-task.
  sessionEndedMessage: string | null;

  /// True when this employee holds the permission.
  can: (permission: string) => boolean;
  canAny: (permissions: string[]) => boolean;

  bootstrap: () => Promise<void>;
  loginWithPassword: (username: string, password: string) => Promise<void>;
  loginWithPin: (userId: string, pin: string) => Promise<void>;
  logout: () => Promise<void>;
  clearSessionEndedMessage: () => void;
}

function applyAuthPayload(payload: AuthPayload) {
  return {
    status: 'authenticated' as const,
    user: payload.user,
    role: payload.role,
    permissions: new Set(payload.permissions),
    sessionEndedMessage: null,
  };
}

// ---------------------------------------------------------------------------
// The last session the server confirmed, kept for a start-up it cannot answer.
//
// WHY. A till that is refreshed - or reopened - while the server is out of
// reach asks `/auth/me` and gets no answer at all. Treating silence as "not
// signed in" throws the cashier out of a till whose cookie is still perfectly
// valid, with the day's queued sales waiting in the outbox behind a login
// page that cannot log anybody in. So the last CONFIRMED session is written
// down and used only for that case: a network failure. A refusal from the
// server - expired, revoked, suspended - is believed at once and clears it.
//
// WHAT IS KEPT. The name, the role and the permission list: exactly what the
// interface draws. Not a credential: the session itself is the HTTP-only
// cookie, and every request offline is queued and checked by the server when
// it arrives. Someone who edits this in their browser gains nothing.
// ---------------------------------------------------------------------------

const SESSION_SNAPSHOT_KEY = 'yaghi.session.snapshot';

function rememberSession(payload: AuthPayload): void {
  try {
    localStorage.setItem(
      SESSION_SNAPSHOT_KEY,
      JSON.stringify({ user: payload.user, role: payload.role, permissions: payload.permissions }),
    );
  } catch {
    // A browser that cannot store it simply asks the server every time.
  }
}

function forgetSession(): void {
  try {
    localStorage.removeItem(SESSION_SNAPSHOT_KEY);
  } catch {
    // Nothing to forget.
  }
}

function rememberedSession(): AuthPayload | null {
  try {
    const raw = localStorage.getItem(SESSION_SNAPSHOT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { user?: CurrentUser; role?: RoleSummary; permissions?: string[] };
    if (!parsed.user || !parsed.role || !Array.isArray(parsed.permissions)) return null;
    return { user: parsed.user, role: parsed.role, permissions: parsed.permissions } as AuthPayload;
  } catch {
    return null;
  }
}

/// A request that never reached the server, as opposed to one it refused.
function serverUnreachable(error: unknown): boolean {
  return !(error instanceof ApiError) || error.code === 'NETWORK_ERROR' || error.status >= 500;
}

const GUEST_STATE = {
  status: 'guest' as const,
  user: null,
  role: null,
  permissions: new Set<string>(),
};

export const useAuthStore = create<AuthState>((set, get) => ({
  status: 'checking',
  user: null,
  role: null,
  permissions: new Set<string>(),
  sessionEndedMessage: null,

  can: (permission) => get().permissions.has(permission),
  canAny: (permissions) => {
    const held = get().permissions;
    return permissions.some((permission) => held.has(permission));
  },

  /// Called once when the app starts. If the browser still holds a valid
  /// session cookie the employee goes straight back to work; otherwise they
  /// land on the login page. Either way this must never throw.
  bootstrap: async () => {
    try {
      const payload = await api.get<AuthPayload>('/auth/me');
      rememberSession(payload);
      set(applyAuthPayload(payload));
    } catch (error) {
      // The server could not be reached: the last session it confirmed
      // stands until it can be asked again. The server said no: it is gone.
      const remembered = serverUnreachable(error) ? rememberedSession() : null;
      if (remembered) {
        set(applyAuthPayload(remembered));
        return;
      }
      forgetSession();
      set({ ...GUEST_STATE, sessionEndedMessage: null });
    }
  },

  loginWithPassword: async (username, password) => {
    const payload = await api.post<AuthPayload>('/auth/login', { username, password });
    rememberSession(payload);
    set(applyAuthPayload(payload));
  },

  loginWithPin: async (userId, pin) => {
    const payload = await api.post<AuthPayload>('/auth/login-pin', { userId, pin });
    rememberSession(payload);
    set(applyAuthPayload(payload));
  },

  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // If the server cannot be reached we still clear the local state, so the
      // screen is not left showing someone else's name on a shared till.
      if (!(error instanceof ApiError)) throw error;
    } finally {
      forgetSession();
      set({ ...GUEST_STATE, sessionEndedMessage: null });
    }
  },

  clearSessionEndedMessage: () => set({ sessionEndedMessage: null }),
}));

// When the API client gives up on a session - expired, revoked, or the account
// was suspended while someone was working - drop straight to the login page
// with an explanation.
setSessionLostHandler(() => {
  const { status } = useAuthStore.getState();
  forgetSession();
  useAuthStore.setState({
    ...GUEST_STATE,
    // Only explain if they were actually working. Someone who simply opened
    // the app while signed out does not need to be told their session ended.
    sessionEndedMessage: status === 'authenticated' ? 'sessionExpired' : null,
  });
});
